<vs-dialog [config]="{ dialogContainerClass: 'w-[350px] sm:w-[640px] max-w-[90%] max-h-[90%]' }">
  <vs-dialog-header
    class="self-start w-full"
    [config]="{
      mode: 'simple',
      icon: 'edit-01',
      iconTheme: 'primary',
      title: 'Edit Claim Intake',
      subtitle: 'Record the details collected when this claim was reported.',
      showDivider: true
    }"
  ></vs-dialog-header>

  <form [formGroup]="form" class="flex flex-col gap-4 w-full mt-4" (keydown.enter)="$event.preventDefault()">
    <p *ngIf="isLimited" class="text-sm text-gray-600">
      The claim is authorized. Only the diagnosis, cause of failure and correction can change, for the new jobs.
    </p>

    <!-- Dates -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <vs-form-field appearance="responsive" [config]="{ label: 'Loss date' }">
        <vs-input-container>
          <input formControlName="lossDate" class="picker-indicator-hidden" type="date" [max]="today" />
          <button
            vs-icon-button
            size="xs"
            appearance="clear"
            slot="end"
            [disabled]="isLimited"
            [vsMenuTriggerFor]="lossDateMenu"
            #lossDateTrigger="vsMenuTriggerFor"
          >
            <vs-icon vsTooltip="Open date picker" name="calendar" size="16"></vs-icon>
          </button>
        </vs-input-container>
        <ng-template #lossDateMenu>
          <vs-menu>
            <vs-date-picker
              vs-menu-item
              [value]="toPickerDate(form.value.lossDate)"
              [maxDate]="maxDate"
              [range]="false"
              (selectionChanged)="pickDate('lossDate', $any($event), lossDateTrigger)"
            ></vs-date-picker>
          </vs-menu>
        </ng-template>
      </vs-form-field>

      <vs-form-field appearance="responsive" [config]="{ label: 'Date reported' }">
        <vs-input-container>
          <input formControlName="dateReported" class="picker-indicator-hidden" type="date" [max]="today" />
          <button
            vs-icon-button
            size="xs"
            appearance="clear"
            slot="end"
            [disabled]="isLimited"
            [vsMenuTriggerFor]="dateReportedMenu"
            #dateReportedTrigger="vsMenuTriggerFor"
          >
            <vs-icon vsTooltip="Open date picker" name="calendar" size="16"></vs-icon>
          </button>
        </vs-input-container>
        <ng-template #dateReportedMenu>
          <vs-menu>
            <vs-date-picker
              vs-menu-item
              [value]="toPickerDate(form.value.dateReported)"
              [maxDate]="maxDate"
              [range]="false"
              (selectionChanged)="pickDate('dateReported', $any($event), dateReportedTrigger)"
            ></vs-date-picker>
          </vs-menu>
        </ng-template>
        <p *ngIf="form.hasError('dateOrder')" slot="hint">
          <span class="text-danger">The reported date can't be before the loss date.</span>
        </p>
      </vs-form-field>
    </div>

    <!-- Caller -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <vs-form-field appearance="responsive" [config]="{ label: 'Caller' }">
        <input vsInput type="text" formControlName="callerName" placeholder="Caller name" />
        <p *ngIf="form.hasError('callerPair') && form.touched" slot="hint">
          <span class="text-danger">Enter both a caller name and type.</span>
        </p>
      </vs-form-field>

      <vs-form-field appearance="responsive" [config]="{ label: 'Caller type' }">
        <vs-select vsInput formControlName="callerType" [config]="{ placeholder: 'Select type', allowDeselect: true }">
          <li *ngFor="let type of callerTypes" vs-select-option [value]="type.value">{{ type.label }}</li>
        </vs-select>
      </vs-form-field>

      <vs-form-field appearance="responsive" [config]="{ label: 'Caller contact' }">
        <input vsInput type="text" formControlName="callerContact" placeholder="Phone or email" />
      </vs-form-field>
    </div>

    <!-- Repair facility -->
    <vs-form-field appearance="responsive" [config]="{ label: 'Service provider' }">
      <ws-service-provider-picker formControlName="serviceProvider"></ws-service-provider-picker>
    </vs-form-field>

    <vs-form-field appearance="responsive" [config]="{ label: 'Repair order' }">
      <input vsInput type="text" formControlName="repairOrderReference" placeholder="e.g. RO-84721" />
      <p *ngIf="form.controls.repairOrderReference.invalid" slot="hint">
        <span class="text-danger">Maximum character limit reached.</span>
      </p>
    </vs-form-field>

    <!-- Future dates are fine here -->
    <vs-form-field appearance="responsive" [config]="{ label: config.repairDeadline.label }">
      <vs-input-container>
        <input formControlName="repairDeadline" class="picker-indicator-hidden" type="date" />
        <button
          vs-icon-button
          size="xs"
          appearance="clear"
          slot="end"
          [vsMenuTriggerFor]="repairDeadlineMenu"
          #repairDeadlineTrigger="vsMenuTriggerFor"
        >
          <vs-icon [vsTooltip]="config.repairDeadline.openPicker" name="calendar" size="16"></vs-icon>
        </button>
      </vs-input-container>
      <ng-template #repairDeadlineMenu>
        <vs-menu>
          <vs-date-picker
            vs-menu-item
            [value]="toPickerDate(form.value.repairDeadline)"
            [range]="false"
            (selectionChanged)="pickDate('repairDeadline', $any($event), repairDeadlineTrigger)"
          ></vs-date-picker>
        </vs-menu>
      </ng-template>
      <p slot="hint" class="text-gray-500">{{ config.repairDeadline.hint }}</p>
    </vs-form-field>

    <!-- Usage: only when the asset's property set has a scalar dimension -->
    <div *ngIf="usageDimensions.length" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <vs-form-field appearance="responsive" [config]="{ label: 'Usage at loss' }">
        <vs-select vsInput formControlName="usageDimensionId" [config]="{ placeholder: 'Select reading' }">
          <li *ngFor="let dimension of usageDimensions" vs-select-option [value]="dimension.id">
            {{ dimension.title }}
            <span *ngIf="dimension.atSale !== null && dimension.atSale !== undefined" class="text-gray-500"
              >&nbsp;·&nbsp;{{ config.usage.atSale((dimension.atSale | number) ?? '') }}</span
            >
          </li>
        </vs-select>
      </vs-form-field>

      <vs-form-field appearance="responsive" [config]="{ label: 'Reading' }">
        <input vsInput type="number" min="0" formControlName="usageValue" placeholder="e.g. 84210" />
        <p *ngIf="form.hasError('usagePair') && form.touched" slot="hint">
          <span class="text-danger">{{ config.usage.pairError }}</span>
        </p>
      </vs-form-field>
    </div>

    <!-- Findings -->
    <vs-form-field appearance="responsive" [config]="{ label: 'Diagnosis' }">
      <textarea
        vsInput
        minRows="2"
        maxRows="4"
        formControlName="diagnosis"
        placeholder="What the shop found"
      ></textarea>
    </vs-form-field>

    <vs-form-field appearance="responsive" [config]="{ label: 'Cause of failure' }">
      <textarea vsInput minRows="2" maxRows="4" formControlName="causeOfFailure" placeholder="Why it failed"></textarea>
    </vs-form-field>

    <vs-form-field appearance="responsive" [config]="{ label: 'Proposed correction' }">
      <textarea
        vsInput
        minRows="2"
        maxRows="4"
        formControlName="correction"
        placeholder="What the shop proposes to fix"
      ></textarea>
      <p slot="hint" class="text-xs text-gray-500">Detailed jobs and costs are added under Claim Items.</p>
    </vs-form-field>

    <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
      <button
        vs-button
        type="button"
        appearance="outline"
        class="w-full sm:w-auto"
        [disabled]="saving"
        (click)="close()"
      >
        Cancel
      </button>

      <button
        vs-button
        type="button"
        theme="primary"
        class="w-full sm:w-auto"
        [disabled]="saving || form.invalid"
        (click)="submit()"
      >
        Save
      </button>
    </div>
  </form>
</vs-dialog>
